import {useMemo} from 'react';

/**
 * The most instances a set may create in one request. Decided by product rather
 * than by the walker: above this, creation is refused with a message naming the
 * real figure. Must stay in step with RecurrenceExpander.MAX_OCCURRENCES.
 */
export const MAX_OCCURRENCES = 500;

/**
 * How far the client keeps counting purely so a refusal can name a number.
 * The ceiling stops the set being CREATED; it should not stop us telling the user
 * they asked for 520. This is date arithmetic with no I/O, so probing further is
 * cheap, and the bound keeps a badly-configured daily-for-a-decade rule from
 * spinning.
 */
export const PROBE_LIMIT = 2000;

/**
 * How far the ADJUSTED walk may run. Eight times the ceiling, because a company
 * may work as little as one day in seven: five hundred tasks can sit inside three
 * and a half thousand dates. Mirrors RecurrenceExpander.WALK_LIMIT.
 *
 * Distinct from PROBE_LIMIT, which bounds the RAW walk and exists only so an
 * over-ceiling rule can be reported as a real number.
 */
export const WALK_LIMIT = 4000;

/** What becomes of the dates the pattern puts on days the company does not work. */
export type NonWorkingDayAction = 'KEEP' | 'SKIP' | 'MOVE';

/** How far the move walk looks for a working day. Mirrors the backend's cap. */
const MAX_DAYS_WALKED = 3650;

export interface TaskSetFormState {
	frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
	intervalLength: number;
	/**
	 * WEEKLY only. Split from monthlyWeekday for the same reason RecurrenceRule
	 * splits them: a weekly rule falls on several days, a monthly nth-weekday rule
	 * falls on exactly one, and one field for both would make "the last Friday and
	 * Monday of the month" expressible.
	 */
	weekdays: string[];
	/** MONTHLY nth-weekday / last-weekday only. */
	monthlyWeekday: string | null;
	monthlyMode: 'DAY_OF_MONTH' | 'NTH_WEEKDAY' | 'LAST_WEEKDAY' | null;
	dayOfMonth: number | null;
	weekOrdinal: number | null;
	seedDate: string;
	boundType: 'COUNT' | 'DATE';
	boundCount: number | null;
	boundEndDate: string | null;
	/**
	 * Optional, and absent reads as KEEP. The edit form builds one of these to
	 * describe a stored set's pattern and has nothing to say about the choice, so
	 * requiring it would make that literal a compile error for no gain.
	 *
	 * KEEP is the identity: with it the adjusted list is the expanded list.
	 */
	nonWorkingDayAction?: NonWorkingDayAction;
}

const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

/** Monday-based index, so it lines up with DAY_ORDER and with the backend. */
const mondayIndex = (d: Date): number => (d.getUTCDay() + 6) % 7;

const parseIso = (iso: string): Date => {
	const [year, month, day] = iso.split('-').map(Number);
	return new Date(Date.UTC(year, month - 1, day));
};

const toIso = (d: Date): string =>
	`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

const lastDayOfMonth = (year: number, month: number): number => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

const nthWeekdayOfMonth = (year: number, month: number, weekday: string, ordinal: number): Date => {
	const target = DAY_ORDER.indexOf(weekday);
	const first = new Date(Date.UTC(year, month, 1));
	const offset = (target - mondayIndex(first) + 7) % 7;
	return new Date(Date.UTC(year, month, 1 + offset + (ordinal - 1) * 7));
};

const lastWeekdayOfMonth = (year: number, month: number, weekday: string): Date => {
	const target = DAY_ORDER.indexOf(weekday);
	const last = new Date(Date.UTC(year, month, lastDayOfMonth(year, month)));
	const back = (mondayIndex(last) - target + 7) % 7;
	return new Date(Date.UTC(year, month, last.getUTCDate() - back));
};

/** Monday-based indices of the selected weekdays, in week order. */
const selectedDayIndices = (weekdays: readonly (string | null)[] | null | undefined): number[] =>
	DAY_ORDER.map((_, i) => i).filter(i => (weekdays || []).includes(DAY_ORDER[i]));

/** The first date on or after `from` whose weekday is selected. */
const nextOrSameSelectedWeekday = (from: Date, weekdays: string[]): Date => {
	for (let i = 0; i < 7; i++) {
		const candidate = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + i));
		if (weekdays.includes(DAY_ORDER[mondayIndex(candidate)])) {
			return candidate;
		}
	}
	return from;
};

/**
 * Mirrors RecurrenceExpander.weeklyOccurrence. The period-th occurrence counted
 * from `first`, not the next one after some cursor.
 *
 * Indexed because the caller asks for an arbitrary period: the slot within the
 * selected-day cycle gives the weekday, and how many times the cycle has wrapped
 * gives the weeks, times the interval. So the interval spaces the WEEKS, not the
 * occurrences -- "every two weeks on Mon and Wed" is Mon, Wed, skip a week, Mon,
 * Wed. Written iteratively it would return the same date for every period once
 * the answer fell inside first's own week.
 */
const weeklyOccurrence = (form: TaskSetFormState, picked: number[], first: Date, period: number): Date => {
	if (picked.length === 0) {
		return first;
	}
	// nextOrSameSelectedWeekday put `first` on a selected day, so this is never -1.
	const firstIndex = picked.indexOf(mondayIndex(first));
	const slot = firstIndex + period;
	const weeksAhead = Math.floor(slot / picked.length) * form.intervalLength;
	const dayInWeek = picked[slot % picked.length];

	const weekStart = first.getUTCDate() - mondayIndex(first);
	return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), weekStart + weeksAhead * 7 + dayInWeek));
};

/**
 * Mirrors RecurrenceExpander.cycleOf: which cycle of the pattern the period-th
 * occurrence falls in, counting from 0.
 *
 * "Ends after N times" means N cycles, not N tasks. Only a weekly rule on several
 * days has more than one occurrence per cycle, so for every other rule the two
 * are the same number. The week holding `first` is cycle 0 even when it is
 * partial, so it counts as one of the N.
 */
const cycleOf = (form: TaskSetFormState, picked: number[], first: Date, period: number): number => {
	if (form.frequency !== 'WEEKLY') {
		return period;
	}
	return Math.floor((picked.indexOf(mondayIndex(first)) + period) / picked.length);
};

/** The calendar month `step` months after `from`, as a year/month pair. */
const monthsFrom = (from: Date, step: number): {year: number; month: number} => {
	const monthIndex = from.getUTCMonth() + step;
	return {
		year: from.getUTCFullYear() + Math.floor(monthIndex / 12),
		month: ((monthIndex % 12) + 12) % 12,
	};
};

/**
 * Mirrors RecurrenceExpander.monthlyOccurrence: resolves the rule's day within a
 * given month. Only the month matters -- the day the caller arrived with is
 * discarded, which is why "the last Friday" lands correctly in every month
 * rather than drifting from the seed's own date.
 */
const monthlyOccurrenceIn = (form: TaskSetFormState, {year, month}: {year: number; month: number}): Date => {
	if (form.monthlyMode === 'NTH_WEEKDAY') {
		return nthWeekdayOfMonth(year, month, form.monthlyWeekday as string, form.weekOrdinal as number);
	}
	if (form.monthlyMode === 'LAST_WEEKDAY') {
		return lastWeekdayOfMonth(year, month, form.monthlyWeekday as string);
	}
	// Clamp rather than roll: "the 31st" in February means the 28th, not 3 March.
	return new Date(Date.UTC(year, month, Math.min(form.dayOfMonth as number, lastDayOfMonth(year, month))));
};

const occurrenceForPeriod = (form: TaskSetFormState, picked: number[], seed: Date, period: number): Date => {
	const step = form.intervalLength * period;

	if (form.frequency === 'DAILY') {
		return new Date(Date.UTC(seed.getUTCFullYear(), seed.getUTCMonth(), seed.getUTCDate() + step));
	}
	if (form.frequency === 'WEEKLY') {
		return weeklyOccurrence(form, picked, seed, period);
	}
	if (form.frequency === 'YEARLY') {
		// Clamp rather than roll, matching Java's plusYears: 29 February in a
		// non-leap year is 28 February. Date.UTC would roll it to 1 March and the
		// preview would name dates the backend never creates.
		const yearlyYear = seed.getUTCFullYear() + step;
		const yearlyMonth = seed.getUTCMonth();
		return new Date(
			Date.UTC(yearlyYear, yearlyMonth, Math.min(seed.getUTCDate(), lastDayOfMonth(yearlyYear, yearlyMonth)))
		);
	}

	return monthlyOccurrenceIn(form, monthsFrom(seed, step));
};

/**
 * Mirrors RecurrenceExpander.firstOccurrence: the earliest date satisfying the
 * rule that is not before the seed.
 *
 * The seed is where the series starts looking, not necessarily its first
 * occurrence. Both the weekly and the monthly rule snap FORWARD, so a set never
 * starts earlier than the date the user picked -- and instance one always obeys
 * the pattern the config view describes.
 */
const firstOccurrence = (form: TaskSetFormState, seed: Date): Date => {
	if (form.frequency === 'WEEKLY') {
		return form.weekdays.length > 0 ? nextOrSameSelectedWeekday(seed, form.weekdays) : seed;
	}
	// A null mode is a half-built form; there is no rule to satisfy yet.
	if (form.frequency === 'MONTHLY' && form.monthlyMode) {
		const candidate = monthlyOccurrenceIn(form, monthsFrom(seed, 0));
		return candidate.getTime() < seed.getTime() ? monthlyOccurrenceIn(form, monthsFrom(seed, 1)) : candidate;
	}
	return seed;
};

export interface NonWorkingDayAdjustment {
	/**
	 * How many dates the RAW walk produced. The probe-limit flag reads this, not
	 * the kept list: once Skip drops dates the kept count is comfortably under the
	 * bound even when the raw walk was cut at it, and the drawer would then print
	 * a truncated figure as though it were exact.
	 */
	rawCount: number;
	/** The dates a task is created on, ascending. */
	dates: string[];
	/** Dates the pattern gave that produce no task at all. */
	skipped: number;
	/** Dates kept, but on a different day from the one the pattern gave. */
	moved: number;
	/**
	 * Of the skipped, how many a MOVE carried past the series end date.
	 *
	 * Separate from the rest because the reader is owed the real reason: a date
	 * dropped by the end wall collided with nothing, and telling them it did
	 * sends them looking for a task that is not there.
	 */
	pastEnd: number;
}

/**
 * True when the company works no day of one week from `from`.
 *
 * Only sound because the predicate answers on the day of the week alone -- it is
 * built from the company's weekday minutes. See the call site below.
 */
const worksNoDay = (isWorkingDay: (iso: string) => boolean, from: string): boolean => {
	const start = parseIso(from);
	for (let day = 0; day < 7; day++) {
		const probe = toIso(new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + day)));
		if (isWorkingDay(probe)) {
			return false;
		}
	}
	return true;
};

/** The first working day strictly after `iso`, or null when there is none. */
const nextWorkingDay = (iso: string, isWorkingDay: (iso: string) => boolean): string | null => {
	const start = parseIso(iso);
	for (let walked = 1; walked <= MAX_DAYS_WALKED; walked++) {
		const candidate = toIso(new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + walked)));
		if (isWorkingDay(candidate)) {
			return candidate;
		}
	}
	return null;
};

/**
 * Mirrors NonWorkingDayAdjuster on the backend. Kept in step with it by sharing
 * the same test cases -- if one changes, both change.
 *
 * `target` is how many dates to keep before stopping, or null for all of them.
 * Stopping here rather than trimming the result afterwards is what keeps the two
 * counts honest: a count-bounded set walks a list far longer than it keeps, and
 * trimming would report every non-working day in the whole probe walk as
 * skipped rather than the ones that stood between the tasks actually created.
 *
 * The collision rule is ownership, not walk order: a day the pattern itself
 * lands on belongs to the pattern, whether or not the walk has reached it yet,
 * and a moved date that would land there gives way. Deciding by walk order would
 * let Saturday's moved instance take the Monday and evict the Monday the user
 * actually asked for. On a daily pattern that means Move produces exactly what
 * Skip produces -- which the summary has to say, because a reader who chose Move
 * and silently got Skip has been told nothing.
 */
export const adjustForNonWorkingDays = (
	dates: string[],
	action: NonWorkingDayAction,
	isWorkingDay: (iso: string) => boolean,
	endDate: string | null,
	target: number | null = null
): NonWorkingDayAdjustment => {
	if (action === 'KEEP') {
		return {dates: [...dates], skipped: 0, moved: 0, pastEnd: 0, rawCount: dates.length};
	}

	/* A company that works no day at all. Every MOVE below would look
	   MAX_DAYS_WALKED days ahead for a landing day that cannot exist, once per
	   date, allocating a Date and an ISO string each probe -- and the memo is keyed
	   on the form, so it reran on every keystroke in the drawer. A single week
	   settles it, because the predicate answers on the day of the week alone.

	   The answer is what the walk produces anyway: nothing lands, so every date is
	   skipped -- but a target of zero stops it before the first date. */
	if (action === 'MOVE' && dates.length > 0 && worksNoDay(isWorkingDay, dates[0])) {
		const none = target !== null && target <= 0 ? 0 : dates.length;
		return {dates: [], skipped: none, moved: 0, pastEnd: 0, rawCount: dates.length};
	}

	/* The days the pattern claims for itself. Built up front because a moved date
	   has to lose to a working day LATER in the list as well as to an earlier
	   one, and the walk has not seen the later ones yet. */
	const ownedByThePattern = action === 'MOVE' ? new Set(dates.filter(date => isWorkingDay(date))) : new Set<string>();

	const taken: string[] = [];
	const seen = new Set<string>();
	let skipped = 0;
	let moved = 0;
	let pastEnd = 0;

	for (const date of dates) {
		if (target !== null && taken.length >= target) {
			break;
		}

		let candidate: string | null;
		if (isWorkingDay(date)) {
			candidate = date;
		} else if (action === 'SKIP') {
			skipped += 1;
			continue;
		} else {
			candidate = nextWorkingDay(date, isWorkingDay);
			// The pattern's own instance owns that day; this one gives way.
			if (candidate !== null && ownedByThePattern.has(candidate)) {
				candidate = null;
			}
		}

		// Null is either a company that works no day at all or a landing day the
		// pattern already owns; both produce no task.
		if (candidate === null) {
			skipped += 1;
			continue;
		}
		/* Past the series end -- ISO dates compare correctly as strings, so no
		   parse. Counted apart: nothing collided, the end date simply arrived
		   first, and a reader told otherwise goes looking for a task that is not
		   there. */
		if (endDate !== null && candidate > endDate) {
			skipped += 1;
			pastEnd += 1;
			continue;
		}
		if (seen.has(candidate)) {
			skipped += 1;
			continue;
		}
		seen.add(candidate);
		taken.push(candidate);
		if (candidate !== date) {
			moved += 1;
		}
	}

	return {dates: taken, skipped, moved, pastEnd, rawCount: dates.length};
};

/** A date the rule produces, and which cycle (repeat) of the pattern it falls in, from 0. */
export interface DatedCycle {
	date: string;
	cycle: number;
}

/**
 * Mirrors RecurrenceExpander on the backend. Kept in step with it by sharing the
 * same test cases -- if one changes, both change.
 *
 * `cycleLimit` is the COUNT bound, or null to ignore it and run to PROBE_LIMIT.
 * A skipping set needs the second form: it considers more dates than it keeps.
 */
const walkCycles = (form: TaskSetFormState, cycleLimit: number | null, limit: number = PROBE_LIMIT): DatedCycle[] => {
	if (!form.seedDate) {
		return [];
	}
	if (form.frequency === 'WEEKLY' && form.weekdays.length === 0) {
		return [];
	}
	if (!form.intervalLength || form.intervalLength < 1) {
		// An interval below one never advances, so the walk would run to the probe
		// limit. Validation refuses this too; returning nothing keeps the preview
		// from spinning while the user is mid-edit.
		return [];
	}

	// Snapped to the first date the rule actually allows, so period 0 obeys the
	// pattern rather than being the raw seed.
	const first = firstOccurrence(form, parseIso(form.seedDate));
	const end = form.boundType === 'DATE' && form.boundEndDate ? parseIso(form.boundEndDate) : null;
	// Fixed for the whole walk, so worked out once rather than per period.
	const picked = selectedDayIndices(form.weekdays);

	const dates: DatedCycle[] = [];
	let current = first;
	let period = 0;

	// Counts past the ceiling on purpose, up to PROBE_LIMIT, so an over-ceiling
	// rule can be reported as "520 tasks" rather than "more than 500".
	while (dates.length < limit) {
		const cycle = cycleOf(form, picked, first, period);
		if (cycleLimit !== null && cycle >= cycleLimit) {
			break;
		}
		if (end && current.getTime() > end.getTime()) {
			break;
		}

		dates.push({date: toIso(current), cycle});
		period += 1;
		current = occurrenceForPeriod(form, picked, first, period);
	}

	return dates;
};

/** The dates alone. See walkCycles. */
const walk = (form: TaskSetFormState, cycleLimit: number | null, limit: number = PROBE_LIMIT): string[] =>
	walkCycles(form, cycleLimit, limit).map(entry => entry.date);

/**
 * The dates a set will actually create, and what the choice did to the rest.
 *
 * A COUNT bound is the interesting case. The stored count is a number of CYCLES,
 * and the user is owed as many tasks as those cycles describe -- so a skipped
 * date must not cost them one. The walk runs wide and the adjuster stops once it
 * has as many tasks as the unfiltered pattern would have given. Mirrors
 * TaskSetLogic.plannedDates.
 */
const expandAdjusted = (form: TaskSetFormState, isWorkingDay?: (iso: string) => boolean): NonWorkingDayAdjustment => {
	const raw = walk(form, form.boundType === 'COUNT' ? form.boundCount || 0 : null);
	const action = form.nonWorkingDayAction || 'KEEP';
	if (action === 'KEEP' || !isWorkingDay) {
		return {dates: raw, skipped: 0, moved: 0, pastEnd: 0, rawCount: raw.length};
	}
	if (form.boundType !== 'COUNT') {
		/* Re-walked to WALK_LIMIT rather than reusing `raw`, which stops at
		   PROBE_LIMIT. A date-bounded set that considers more dates than it keeps
		   can run past 2,000 and still create well under the ceiling -- a company
		   working Mondays only over twenty years is the shape -- and truncating
		   there left the drawer promising a few hundred tasks with Create enabled
		   while the backend, which walks to WALK_LIMIT, refused the same set. The
		   two mirrors have to stop in the same place. */
		const wide = walk(form, null, WALK_LIMIT);
		// rawCount stays the RAW walk's length, not the wide one's: it answers
		// "did the rule run past the bound we can report on", which is about the
		// pattern, not about how far the adjuster had to look.
		return {...adjustForNonWorkingDays(wide, action, isWorkingDay, form.boundEndDate), rawCount: raw.length};
	}
	/* Widened in steps rather than walking the full limit once. The memo is keyed
	   on `form`, so every keystroke in the drawer -- name, estimate, description --
	   re-runs this; a three-task set should cost a handful of cycles, not four
	   thousand. Each round doubles, so the successful walk is at worst twice the
	   one needed. Mirrors NonWorkingDayAdjuster.plannedDates. */
	for (let cycles = raw.length * 2; ; cycles *= 2) {
		const lastRound = cycles >= WALK_LIMIT;
		const wide = adjustForNonWorkingDays(
			walk(form, lastRound ? null : cycles, WALK_LIMIT),
			action,
			isWorkingDay,
			null,
			raw.length
		);
		// Short of the target only means "walk further" while there is further to
		// walk. A weekly Saturday set that skips keeps nothing at any length.
		if (wide.dates.length >= raw.length || lastRound) {
			return {...wide, rawCount: raw.length};
		}
	}
};

/**
 * The dates the rule produces, with the non-working-day choice applied.
 *
 * `isWorkingDay` is optional so every existing caller -- the edit form's pattern
 * description among them -- keeps getting the raw pattern it asks for. Passing
 * it is what turns a pattern into the dates a set will actually create.
 */
export const expandRecurrence = (form: TaskSetFormState, isWorkingDay?: (iso: string) => boolean): string[] =>
	expandAdjusted(form, isWorkingDay).dates;

/**
 * The dates a set creates, each with the repeat of the pattern it belongs to, from 0.
 *
 * Without a non-working-day adjustment a date's repeat is its cycle. With one, a
 * set is owed as many tasks per repeat as the unfiltered pattern gives, so repeat
 * n ends at the task that brings the adjusted list up to what n raw repeats
 * produce -- the same rule the backend's planned dates follow, so a repeat's end
 * here is where the server ends a set of that many times.
 */
export const expandRecurrenceCycles = (form: TaskSetFormState, isWorkingDay?: (iso: string) => boolean): DatedCycle[] => {
	const raw = walkCycles(form, form.boundType === 'COUNT' ? form.boundCount || 0 : null);
	const action = form.nonWorkingDayAction || 'KEEP';
	if (action === 'KEEP' || !isWorkingDay) {
		return raw;
	}
	// owedThrough[c]: how many tasks repeats 0..c are owed, from the raw walk.
	const owedThrough: number[] = [];
	raw.forEach(entry => {
		owedThrough[entry.cycle] = (owedThrough[entry.cycle] ?? 0) + 1;
	});
	for (let cycle = 1; cycle < owedThrough.length; cycle++) {
		owedThrough[cycle] = (owedThrough[cycle] ?? 0) + (owedThrough[cycle - 1] ?? 0);
	}
	let cycle = 0;
	return expandRecurrence(form, isWorkingDay).map((date, index) => {
		while (cycle < owedThrough.length - 1 && index >= owedThrough[cycle]) {
			cycle += 1;
		}
		return {date, cycle};
	});
};

export interface TaskSetPreview {
	dates: string[];
	/** What the rule asks for, which may be above the ceiling. */
	count: number;
	overCeiling: boolean;
	/** True when the count is itself a floor rather than exact -- see PROBE_LIMIT. */
	countIsAtProbeLimit: boolean;
	/** Dates the pattern gave that produce no task, under the chosen action. */
	skipped: number;
	/** Dates kept, but moved off the day the pattern gave. */
	moved: number;
	/** Of the skipped, how many a MOVE carried past the end date. */
	pastEnd: number;
}

export const useTaskSetPreview = (form: TaskSetFormState, isWorkingDay?: (iso: string) => boolean): TaskSetPreview =>
	useMemo(() => {
		const adjusted = expandAdjusted(form, isWorkingDay);
		const dates = adjusted.dates;
		const overCeiling = dates.length > MAX_OCCURRENCES;
		return {
			// Never show occurrences that would not be created as if they were.
			dates: overCeiling ? dates.slice(0, MAX_OCCURRENCES) : dates,
			count: dates.length,
			overCeiling,
			// Read off the RAW walk. Once Skip drops dates the kept count is under
			// the bound even when the raw walk was cut at it, and the drawer would
			// then print a truncated figure as though it were the real one.
			countIsAtProbeLimit: adjusted.rawCount >= PROBE_LIMIT,
			skipped: adjusted.skipped,
			moved: adjusted.moved,
			pastEnd: adjusted.pastEnd,
		};
		// isWorkingDay is derived from the company's working hours, which do not
		// change while the drawer is open; a caller that rebuilds the closure each
		// render would defeat the memo entirely, so it memoises its own.
	}, [form, isWorkingDay]);

/** The stored fields `plannedInstanceCount` needs. Structural, like DescribableTaskSet. */
export interface CountableTaskSet {
	frequency?: string | null;
	weekdays?: readonly (string | null)[] | null;
	seedDate?: string | null;
	boundType?: string | null;
	boundCount?: number | null;
}

/**
 * How many instances a count-bounded set's rule produces, or null where the rule
 * states no length.
 *
 * The stored count is a number of cycles, not of instances. For every rule but a
 * weekly one on several days a cycle holds one instance, so the count is the
 * answer. A weekly cycle holds one instance per selected day, less the selected
 * days that fell before the first instance in week 1: "Mon, Tue, Wed, 2 times"
 * from a Wednesday is Wed, then Mon to Wed -- four, not six.
 *
 * Null for a weekly set whose weekdays are missing, or a weekly set on several
 * days whose seed is missing: without them the number of instances per week, or
 * the partial first week, is unknowable. Answering with the stored count instead
 * would state a number of weeks as a number of instances and mislabel every chip,
 * so a caller that forgets to select these fields gets no total rather than a
 * wrong one.
 */
export const plannedInstanceCount = (set: CountableTaskSet): number | null => {
	if (set.boundType !== 'COUNT' || set.boundCount == null) {
		return null;
	}
	if (set.frequency === 'WEEKLY' && set.weekdays == null) {
		return null;
	}
	const picked = selectedDayIndices(set.weekdays);
	if (set.frequency !== 'WEEKLY' || picked.length <= 1) {
		return set.boundCount;
	}
	if (!set.seedDate) {
		return null;
	}
	const first = nextOrSameSelectedWeekday(
		parseIso(set.seedDate),
		picked.map(i => DAY_ORDER[i])
	);
	const skippedInFirstWeek = picked.indexOf(mondayIndex(first));
	return set.boundCount * picked.length - skippedInFirstWeek;
};

/**
 * What the drawer says about week 1 when it holds fewer than every selected day.
 *
 * Only a weekly rule on several days can have a partial first week, and only
 * when the seed lands after the first selected day of its own week. A seed past
 * the LAST selected day snaps to the next week, which is full, so there is
 * nothing to warn about.
 */
export interface PartialFirstWeek {
	/** The seed date, as picked -- which is also the first instance. */
	seedDate: string;
	/** The selected days that week 1 actually holds, Monday first. */
	daysInFirstWeek: string[];
	/**
	 * The first selected day of the following week: where a full week 1 would
	 * start. Null when an end date falls before it, because starting there would
	 * leave the set with no instances at all.
	 */
	fullWeekStart: string | null;
}

export const partialFirstWeek = (form: TaskSetFormState): PartialFirstWeek | null => {
	if (form.frequency !== 'WEEKLY' || !form.seedDate) {
		return null;
	}
	const picked = selectedDayIndices(form.weekdays);
	if (picked.length <= 1) {
		return null;
	}
	const seed = parseIso(form.seedDate);
	// A half-typed date parses to NaN, which fails both range checks below and
	// would fall through to a nonsense alert.
	if (isNaN(seed.getTime())) {
		return null;
	}
	const seedIndex = mondayIndex(seed);
	if (seedIndex <= picked[0] || seedIndex > picked[picked.length - 1]) {
		return null;
	}
	const weekStart = new Date(Date.UTC(seed.getUTCFullYear(), seed.getUTCMonth(), seed.getUTCDate() - seedIndex));
	const nextWeekFirstDay = new Date(
		Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), weekStart.getUTCDate() + 7 + picked[0])
	);
	const endsBeforeFullWeek =
		form.boundType === 'DATE' && !!form.boundEndDate && parseIso(form.boundEndDate).getTime() < nextWeekFirstDay.getTime();
	return {
		seedDate: form.seedDate,
		daysInFirstWeek: picked.filter(i => i >= seedIndex).map(i => DAY_ORDER[i]),
		fullWeekStart: endsBeforeFullWeek ? null : toIso(nextWeekFirstDay),
	};
};
